import { Button, Chip, type ChipColor } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { fetchMe, type Me } from "../../api/profile.service";
import { PageHeader } from "../../components/PageHeader";
import { useAuthActions } from "../../contexts/AuthContext";
import { formatDateTimeShort } from "../../utils/dateFormatter";
import styles from "./MyAccount.module.css";

type StatusChip = {
  label: string;
  color: ChipColor;
};

const STATUS_CHIPS: Record<NonNullable<Me["status"]>, StatusChip> = {
  verified: { label: "Verified", color: "success" },
  unverified: { label: "Unverified", color: "warning" },
  rejected: { label: "Rejected", color: "error" },
};

function renderStatus(status: Me["status"]) {
  if (!status) {
    return <span className={styles.infoValueMuted}>Unknown</span>;
  }
  const chip = STATUS_CHIPS[status];
  return (
    <Chip color={chip.color} size="small">
      {chip.label}
    </Chip>
  );
}

function formatUnixTimestamp(unixSeconds: number): string {
  return formatDateTimeShort(new Date(unixSeconds * 1000).toISOString());
}

function formatRemainingLifetime(exp: number): string {
  const secondsRemaining = exp - Math.floor(Date.now() / 1000);
  if (secondsRemaining <= 0) {
    return "Expired";
  }
  const hours = Math.floor(secondsRemaining / 3600);
  const minutes = Math.floor((secondsRemaining % 3600) / 60);
  const seconds = secondsRemaining % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (hours > 0 || minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return `expires in ${parts.join(" ")}`;
}

function renderOptional(value: string | null | undefined) {
  if (!value) {
    return <span className={styles.infoValueMuted}>—</span>;
  }
  return <span className={styles.infoValue}>{value}</span>;
}

function renderOptionalMono(value: string | null | undefined) {
  if (!value) {
    return <span className={styles.infoValueMuted}>—</span>;
  }
  return <span className={`${styles.infoValue} ${styles.infoValueMono}`}>{value}</span>;
}

function renderOptionalDate(value: string | null | undefined) {
  if (!value) {
    return <span className={styles.infoValueMuted}>—</span>;
  }
  return <span className={styles.infoValue}>{formatDateTimeShort(value)}</span>;
}

type ImpersonationBannerProps = {
  impersonatedBy: string | null;
  onExit: () => void;
};

function ImpersonationBanner({ impersonatedBy, onExit }: ImpersonationBannerProps) {
  return (
    <div className={styles.impersonationBanner} role="alert">
      <h2 className={styles.impersonationHeading}>You are currently impersonating another user</h2>
      <p className={styles.impersonationBody}>
        {impersonatedBy
          ? `Impersonation was initiated by ${impersonatedBy}.`
          : "Impersonation was initiated by a staff user."}
      </p>
      <div className={styles.impersonationActions}>
        <Button variant="contained" color="warning" onClick={onExit}>
          Exit impersonation
        </Button>
        <p className={styles.impersonationNote}>
          Exiting signs you out of staff-ui; sign back in as yourself to continue.
        </p>
      </div>
    </div>
  );
}

type IdentitySectionProps = {
  me: Me;
};

function IdentitySection({ me }: IdentitySectionProps) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Identity</h2>
      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Name</span>
          {renderOptional(me.name)}
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Email</span>
          {renderOptional(me.email)}
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Status</span>
          {renderStatus(me.status)}
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>User UUID</span>
          {renderOptionalMono(me.userId)}
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>External Auth ID</span>
          {renderOptionalMono(me.externalAuthId)}
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Source</span>
          {renderOptional(me.source)}
        </div>
      </div>
    </section>
  );
}

type RolesSectionProps = {
  roles: Me["roles"];
};

function RolesSection({ roles }: RolesSectionProps) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Platform roles</h2>
      {roles.length === 0 ? (
        <p className={styles.emptyMessage}>No platform roles assigned.</p>
      ) : (
        <ul className={styles.rolesList}>
          {roles.map(role => (
            <li key={role.name} className={styles.roleItem}>
              <span className={styles.roleName}>{role.name}</span>
              {role.description && (
                <span className={styles.roleDescription}>{role.description}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

type TokenSectionProps = {
  me: Me;
};

function TokenSection({ me }: TokenSectionProps) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Token metadata</h2>
      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Source BFF</span>
          {renderOptional(me.source)}
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Issued at</span>
          <span className={styles.infoValue}>{formatUnixTimestamp(me.iat)}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Expires at</span>
          <span className={styles.infoValue}>{formatUnixTimestamp(me.exp)}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Remaining lifetime</span>
          <span className={styles.infoValue}>{formatRemainingLifetime(me.exp)}</span>
        </div>
      </div>
    </section>
  );
}

type AccountDatesSectionProps = {
  me: Me;
};

function AccountDatesSection({ me }: AccountDatesSectionProps) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Account dates</h2>
      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Created</span>
          {renderOptionalDate(me.createdAt)}
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Last updated</span>
          {renderOptionalDate(me.updatedAt)}
        </div>
      </div>
    </section>
  );
}

function MyAccountPage() {
  const { logout } = useAuthActions();
  const [me, setMe] = useState<Me | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadMe() {
      setIsLoading(true);
      setFetchError(null);
      try {
        const data = await fetchMe();
        if (!cancelled) {
          setMe(data);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Unknown error";
          setFetchError(`Failed to load your account details: ${message}`);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadMe();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  function handleRetry() {
    setReloadToken(current => current + 1);
  }

  function handleExitImpersonation() {
    void logout();
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="My Account"
        subtitle="Your Legal 500 staff profile, roles and session details"
      />

      {isLoading && <p className={styles.loadingMessage}>Loading your account…</p>}

      {fetchError && !isLoading && (
        <div className={styles.errorPanel} role="alert">
          <p className={styles.errorMessage}>{fetchError}</p>
          <Button variant="contained" color="primary" onClick={handleRetry}>
            Retry
          </Button>
        </div>
      )}

      {me && !isLoading && !fetchError && (
        <>
          {me.isImpersonating && (
            <ImpersonationBanner
              impersonatedBy={me.impersonatedBy}
              onExit={handleExitImpersonation}
            />
          )}
          <IdentitySection me={me} />
          <RolesSection roles={me.roles} />
          <TokenSection me={me} />
          <AccountDatesSection me={me} />
        </>
      )}
    </div>
  );
}

export default MyAccountPage;
